import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { API_BASE_URL } from '../config/api';
import { clearAuthSession, getAuthToken } from '../utils/auth';
import { trackEvent } from '../utils/amplitude';
import { getSavedJobs } from '../utils/savedJobs';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import AiProcessingPopup from '../components/interview/AiProcessingPopup';

const FORMAT_OPTIONS = [
  { id: 'quick', label: 'Quick', questions: 5, description: 'Fast practice', capabilityKey: 'allow_interview_quick' },
  { id: 'standard', label: 'Standard', questions: 11, description: 'Balanced prep', capabilityKey: 'allow_interview_standard' },
  { id: 'full', label: 'Full', questions: 20, description: 'Complete simulation', capabilityKey: 'allow_interview_full' },
];

const TOPIC_OPTIONS = [
  { id: 'behavioral', label: 'Behavioral', capabilityKey: 'allow_topic_behavioral' },
  { id: 'hard_skills', label: 'Hard Skills', capabilityKey: 'allow_topic_hard_skills' },
  { id: 'soft_skills', label: 'Soft Skills', capabilityKey: 'allow_topic_soft_skills' },
  { id: 'motivation', label: 'Motivation', capabilityKey: 'allow_topic_motivation' },
];

const isUuid = (value) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const DEFAULT_CAPABILITIES = {
  allow_interview_quick: true,
  allow_interview_standard: true,
  allow_interview_full: true,
  allow_topic_behavioral: true,
  allow_topic_hard_skills: true,
  allow_topic_soft_skills: true,
  allow_topic_motivation: true,
  allow_topic_ai: true,
};

async function fetchProfileCapabilities(token) {
  const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await response.json().catch(() => ({}));
  return { response, data };
}

export default function ChatSettingScreen() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const location = useLocation();
  const authModal = useAuthModal();

  useEffect(() => { trackEvent('interview_setting'); }, []);

  const [interviewFormat, setInterviewFormat] = useState('standard');
  const [questionTopics, setQuestionTopics] = useState(['behavioral', 'hard_skills']);
  const [questionTopicsMix, setQuestionTopicsMix] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState('');
  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [initProcessingStatus, setInitProcessingStatus] = useState(null);

  const source = location.state?.source === 'manual_vacancy' ? 'manual_vacancy' : null;

  const vacancyTitle = useMemo(() => {
    if (location.state?.vacancyTitle) return location.state.vacancyTitle;
    try {
      const currentJobs = JSON.parse(localStorage.getItem('currentJobs') || '[]');
      const currentJob = currentJobs.find((item) => item.id === jobId);
      if (currentJob?.title) return currentJob.title;

      const savedJobs = getSavedJobs();
      const savedJob = savedJobs.find((item) => String(item.id) === String(jobId));
      return savedJob?.title || 'Interview setup';
    } catch {
      return 'Interview setup';
    }
  }, [jobId, location.state]);

  const hasCapability = (capabilityKey) => Boolean(capabilities?.[capabilityKey]);
  const canStart = questionTopicsMix
    ? hasCapability('allow_topic_ai')
    : questionTopics.length > 0;

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;

    let active = true;
    const loadCapabilities = async () => {
      try {
        const { response, data } = await fetchProfileCapabilities(token);

        if (response.status === 401) {
          clearAuthSession();
          return;
        }

        if (response.ok && active && data?.capabilities) {
          setCapabilities((prev) => ({ ...prev, ...data.capabilities }));
        }
      } catch (loadError) {
        console.error('[ChatSetting] capabilities error:', loadError);
      }
    };

    loadCapabilities();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const selectedFormat = FORMAT_OPTIONS.find((option) => option.id === interviewFormat);
    if (selectedFormat && !hasCapability(selectedFormat.capabilityKey)) {
      const fallbackFormat = FORMAT_OPTIONS.find((option) => hasCapability(option.capabilityKey));
      if (fallbackFormat && fallbackFormat.id !== interviewFormat) {
        setInterviewFormat(fallbackFormat.id);
      }
    }

    const allowedTopics = questionTopics.filter((topicId) => {
      const topic = TOPIC_OPTIONS.find((option) => option.id === topicId);
      return topic ? hasCapability(topic.capabilityKey) : false;
    });
    if (allowedTopics.length !== questionTopics.length) {
      setQuestionTopics(allowedTopics);
    }

    if (questionTopicsMix && !hasCapability('allow_topic_ai')) {
      setQuestionTopicsMix(false);
    }
  }, [capabilities]);

  const resolveVacancyId = () => {
    if (isUuid(location.state?.vacancyId)) return location.state.vacancyId;
    if (source === 'manual_vacancy' && isUuid(jobId)) return jobId;

    try {
      const vacancyMap = JSON.parse(localStorage.getItem('vacancyIdsByJob') || '{}');
      const mapped = vacancyMap?.[jobId];
      if (isUuid(mapped)) return mapped;
    } catch {
      // ignore
    }

    if (isUuid(jobId)) return jobId;
    return null;
  };

  const getSearchContext = () => {
    try {
      const currentJobs = JSON.parse(localStorage.getItem('currentJobs') || '[]');
      const currentJob = currentJobs.find((item) => item.id === jobId);
      const requestId = localStorage.getItem('lastSearchRequestId');
      return {
        requestId: isUuid(requestId) ? requestId : null,
        providerJobId: currentJob?.job_id || null,
      };
    } catch {
      return { requestId: null, providerJobId: null };
    }
  };

  const handleStartInterview = async () => {
    if (!canStart || loading) return;

    const token = getAuthToken();
    if (!token) {
      authModal.openAuth('search');
      return;
    }

    const vacancyId = resolveVacancyId();
    if (!vacancyId) {
      setError('Vacancy ID is missing. Open vacancy details first, then try again.');
      return;
    }

    const { requestId, providerJobId } = getSearchContext();
    const payload = {
      vacancy_id: vacancyId,
      vacancy_title: vacancyTitle || null,
      language: 'en',
      interview_format: interviewFormat,
      question_topics: questionTopicsMix ? [] : questionTopics,
      question_topics_mix: questionTopicsMix,
      ...(source && { source }),
      ...(requestId && { request_id: requestId }),
      ...(providerJobId && { job_id: providerJobId }),
    };

    setLoading(true);
    setError('');
    setInitProcessingStatus({
      state: 'active',
      phase: 'bootstrapping',
      title: 'Starting interview session',
      description: 'We are creating your interview session and preparing the first question.',
      label: 'Session setup',
      steps: [
        { id: 'session', label: 'Create interview session', status: 'active' },
        { id: 'context', label: 'Load profile and vacancy context', status: 'pending' },
        { id: 'question', label: 'Open interview workspace', status: 'pending' },
      ],
    });
    try {
      const response = await fetch(`${API_BASE_URL}/api/interviews/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        setInitProcessingStatus(null);
        clearAuthSession();
        authModal.openAuth('search');
        return;
      }

      if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        setInitProcessingStatus(null);
        setLimitErrorMessage(data.detail || 'Monthly interview limit reached. Please upgrade your plan.');
        setShowLimitModal(true);
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setInitProcessingStatus(null);
        setError(data.detail || data.message || 'Failed to start interview.');
        return;
      }

      setInitProcessingStatus({
        state: 'success',
        phase: 'bootstrapping',
        title: 'Interview session is ready',
        description: 'Opening your interview workspace now.',
        label: 'Session setup',
        progress: 100,
        steps: [
          { id: 'session', label: 'Create interview session', status: 'completed' },
          { id: 'context', label: 'Load profile and vacancy context', status: 'completed' },
          { id: 'question', label: 'Open interview workspace', status: 'completed' },
        ],
      });
      navigate(`/chat/${data.interview_id}`);
    } catch (requestError) {
      console.error('[ChatSetting] init error:', requestError);
      setInitProcessingStatus(null);
      setError('Error starting interview.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">Interview setup</h1>
          <p className="text-sm text-gray-500 mt-1">{vacancyTitle}</p>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <section className="rounded-3xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Interview Format</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FORMAT_OPTIONS.map((option) => {
                const isActive = interviewFormat === option.id;
                const isLocked = !hasCapability(option.capabilityKey);
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={isLocked || loading}
                    onClick={() => setInterviewFormat(option.id)}
                    className={`rounded-2xl border px-4 py-3 text-left transition ${
                      isLocked
                        ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
                        : isActive
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className={`text-sm font-semibold ${isActive ? 'text-orange-700' : 'text-gray-900'}`}>{option.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{option.questions} questions</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                    {isLocked && <div className="text-[11px] text-gray-500 mt-1">Upgrade to unlock</div>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="my-6 h-px bg-gray-100" />

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Question Topics</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TOPIC_OPTIONS.map((topic) => {
                const selected = questionTopics.includes(topic.id);
                const isLocked = !hasCapability(topic.capabilityKey);
                return (
                  <button
                    key={topic.id}
                    type="button"
                    disabled={questionTopicsMix || isLocked || loading}
                    onClick={() =>
                      setQuestionTopics((prev) =>
                        prev.includes(topic.id) ? prev.filter((item) => item !== topic.id) : [...prev, topic.id]
                      )
                    }
                    className={`rounded-2xl border px-4 py-3 text-left transition ${
                      questionTopicsMix || isLocked
                        ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-400'
                        : selected
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-200 bg-white hover:border-gray-300 text-gray-800'
                    }`}
                  >
                    <span className="text-sm font-semibold">{topic.label}</span>
                    {isLocked && <div className="text-[11px] text-gray-500 mt-1">Upgrade to unlock</div>}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              disabled={!hasCapability('allow_topic_ai') || loading}
              onClick={() => {
                setQuestionTopicsMix((prev) => !prev);
                if (!questionTopicsMix) {
                  setQuestionTopics([]);
                }
              }}
              className={`w-full rounded-2xl border-2 border-dashed px-4 py-3 text-left transition ${
                !hasCapability('allow_topic_ai')
                  ? 'cursor-not-allowed border-gray-100 bg-gray-50'
                  : questionTopicsMix
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <p className={`text-sm font-semibold ${questionTopicsMix ? 'text-orange-700' : 'text-gray-900'}`}>Mix (AI will choose)</p>
              <p className="text-xs text-gray-500 mt-1">
                {hasCapability('allow_topic_ai') ? 'AI picks a balanced topic sequence.' : 'Upgrade to unlock AI topic mix.'}
              </p>
            </button>
          </div>

          <div className="my-6 h-px bg-gray-100" />

          <button
            type="button"
            onClick={handleStartInterview}
            disabled={!canStart || loading}
            className="btn-orange w-full py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Starting interview...' : 'Start Interview'}
          </button>
        </section>
      </main>

      <Footer />

      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        errorMessage={limitErrorMessage}
      />

      <AiProcessingPopup isOpen={Boolean(initProcessingStatus)} status={initProcessingStatus} />
    </div>
  );
}
